import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import orderTrackingService, { DELIVERY_STAGES } from '../services/orderTrackingService';

const {
  FiPackage, FiTruck, FiClock, FiCheckCircle, FiShoppingBag,
  FiSearch, FiChevronDown, FiChevronUp, FiMapPin, FiUser,
  FiCopy, FiCheck, FiAlertCircle, FiBox, FiCalendar,
  FiHeart, FiGift, FiClipboard, FiRefreshCw, FiNavigation, FiCircle,
} = FiIcons;

const STAGE_ICONS = {
  ordered: FiShoppingBag,
  processing: FiPackage,
  shipped: FiTruck,
  out_for_delivery: FiClock,
  delivered: FiCheckCircle,
};

const STAGE_COLORS = {
  ordered:          { bg: 'bg-blue-50',    text: 'text-blue-600',    ring: 'ring-blue-200'    },
  processing:       { bg: 'bg-amber-50',   text: 'text-amber-600',   ring: 'ring-amber-200'   },
  shipped:          { bg: 'bg-indigo-50',  text: 'text-indigo-600', ring: 'ring-indigo-200'  },
  out_for_delivery: { bg: 'bg-orange-50',  text: 'text-orange-600',  ring: 'ring-orange-200'  },
  delivered:        { bg: 'bg-green-50',   text: 'text-green-600',   ring: 'ring-green-200'   },
};

// ── Status Timeline ─────────────────────────────────────────────────────────────
const StatusTimeline = ({ order }) => {
  const currentIdx = DELIVERY_STAGES.findIndex(s => s.key === order.currentStage);

  return (
    <div className="relative pl-8">
      {/* Vertical line */}
      <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-brand-dark/20" />

      {DELIVERY_STAGES.map((stage, idx) => {
        const isComplete = idx <= currentIdx;
        const isCurrent = idx === currentIdx;
        const Icon = STAGE_ICONS[stage.key] || FiCircle;
        const colors = STAGE_COLORS[stage.key] || STAGE_COLORS.ordered;
        const timelineEntry = order.timeline.find(t => t.stage === stage.key);

        return (
          <div key={stage.key} className={`relative pb-6 ${idx === DELIVERY_STAGES.length - 1 ? 'pb-0' : ''}`}>
            <div
              className={`absolute -left-8 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                isComplete
                  ? `${colors.bg} ${colors.text} ring-2 ${colors.ring}`
                  : 'bg-brand-light text-text-muted/40 ring-1 ring-brand-dark/20'
              } ${isCurrent ? 'scale-125 shadow-lg' : ''}`}
            >
              <SafeIcon icon={Icon} className="w-3 h-3" />
            </div>

            <div className={isComplete ? '' : 'opacity-40'}>
              <div className="flex items-center gap-2 mb-1">
                <p className={`text-sm font-black ${isComplete ? 'text-accent-navy' : 'text-text-muted'}`}>
                  {stage.label}
                </p>
                {isCurrent && (
                  <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${colors.bg} ${colors.text}`}>
                    Current
                  </span>
                )}
              </div>
              {timelineEntry && (
                <>
                  <p className="text-xs text-text-muted font-light">
                    {new Date(timelineEntry.timestamp).toLocaleString()}
                  </p>
                  <p className="text-xs text-text-main mt-1 italic leading-relaxed">
                    {timelineEntry.description}
                  </p>
                </>
              )}
              {!timelineEntry && !isComplete && (
                <p className="text-xs text-text-muted/60 font-light">Pending</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Order Card ─────────────────────────────────────────────────────────────────
const OrderCard = ({ order, formatCurrency }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const colors = STAGE_COLORS[order.currentStage] || STAGE_COLORS.ordered;
  const currentStageInfo = DELIVERY_STAGES.find(s => s.key === order.currentStage) || DELIVERY_STAGES[0];

  const copyTracking = () => {
    navigator.clipboard?.writeText(order.delivery.trackingNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const etaDate = new Date(order.delivery.estimatedDelivery);
  const isDelivered = order.currentStage === 'delivered';

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-[32px] border border-brand-dark shadow-sm overflow-hidden"
    >
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-6 flex items-center justify-between gap-4 hover:bg-brand-light/30 transition-colors"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className={`w-12 h-12 ${colors.bg} ${colors.text} rounded-2xl flex items-center justify-center shrink-0`}>
            <SafeIcon icon={STAGE_ICONS[order.currentStage] || FiBox} className="w-6 h-6" />
          </div>
          <div className="text-left min-w-0">
            <p className="font-serif text-lg text-accent-navy truncate">{order.orderId}</p>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
              {new Date(order.createdAt).toLocaleDateString()} · {order.items.length} {order.items.length === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-lg font-serif text-accent-gold">{formatCurrency(order.total)}</p>
            <p className={`text-[9px] font-black uppercase tracking-widest ${colors.text}`}>
              {currentStageInfo.label}
            </p>
          </div>
          <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-5 h-5 text-text-muted" />
        </div>
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-brand-dark/20"
          >
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Items + timeline */}
              <div className="space-y-6">
                {/* Items */}
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiBox} className="w-3.5 h-3.5" /> Items in This Order
                  </h4>
                  <div className="space-y-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-brand-light rounded-2xl border border-brand-dark/30">
                        {item.image && (
                          <img src={item.image} alt={item.name} className="w-12 h-12 rounded-xl object-cover shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-accent-navy truncate">{item.name}</p>
                          <p className="text-[10px] text-text-muted font-black uppercase tracking-widest">
                            Qty: {item.quantity}
                            {item.selectedVariations && item.selectedVariations.length > 0 && (
                              <span className="ml-2">· {item.selectedVariations.join(', ')}</span>
                            )}
                          </p>
                        </div>
                        <p className="text-sm font-serif text-accent-gold shrink-0">
                          {formatCurrency((item.price || 0) * item.quantity)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiNavigation} className="w-3.5 h-3.5" /> Delivery Progress
                  </h4>
                  <StatusTimeline order={order} />
                </div>
              </div>

              {/* Right: Delivery details */}
              <div className="space-y-6">
                {/* Tracking number */}
                <div className="bg-brand-light rounded-2xl p-5 border border-brand-dark">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-2">Tracking Number</h4>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-mono font-bold text-accent-navy">{order.delivery.trackingNumber}</p>
                    <button
                      onClick={copyTracking}
                      className="p-2 rounded-lg bg-white border border-brand-dark text-text-muted hover:text-accent-gold hover:border-accent-gold transition-all"
                      title="Copy tracking number"
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="mt-3 pt-3 border-t border-brand-dark/20">
                    <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Carrier</p>
                    <p className="text-sm font-bold text-accent-navy mt-1">{order.delivery.carrierLabel}</p>
                  </div>
                </div>

                {/* Estimated delivery */}
                <div className={`rounded-2xl p-5 border ${isDelivered ? 'bg-green-50 border-green-200' : 'bg-accent-gold/10 border-accent-gold/30'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <SafeIcon icon={FiCalendar} className={`w-4 h-4 ${isDelivered ? 'text-green-600' : 'text-accent-gold'}`} />
                    <h4 className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                      {isDelivered ? 'Delivered On' : 'Estimated Delivery'}
                    </h4>
                  </div>
                  <p className="text-lg font-serif text-accent-navy">
                    {etaDate.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  {!isDelivered && (
                    <p className="text-xs text-text-muted mt-1 font-light">
                      {Math.ceil((etaDate - new Date()) / 86400000) > 0
                        ? `${Math.ceil((etaDate - new Date()) / 86400000)} day(s) remaining`
                        : 'Arriving today'}
                    </p>
                  )}
                </div>

                {/* Recipient / address */}
                <div className="bg-white rounded-2xl p-5 border border-brand-dark">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-3">Delivery To</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon={FiUser} className="w-3.5 h-3.5 text-text-muted" />
                      <p className="text-sm font-bold text-accent-navy">{order.delivery.recipientName}</p>
                    </div>
                    {order.delivery.address && (
                      <div className="flex items-start gap-2">
                        <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5 text-text-muted mt-0.5" />
                        <p className="text-xs text-text-muted leading-relaxed">{order.delivery.address}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Gift / instructions */}
                {(order.giftOptions?.giftWrap || order.specialInstructions) && (
                  <div className="space-y-3">
                    {order.giftOptions?.giftWrap && (
                      <div className="bg-accent-gold/10 border border-accent-gold/30 rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <SafeIcon icon={FiGift} className="w-3.5 h-3.5 text-accent-gold" />
                          <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">Gift Wrap</p>
                        </div>
                        <p className="text-xs text-accent-navy font-bold capitalize">{order.giftOptions.wrapStyle} Wrap</p>
                        {order.giftOptions.giftMessage && (
                          <p className="text-xs text-text-muted italic mt-1 leading-relaxed">"{order.giftOptions.giftMessage}"</p>
                        )}
                      </div>
                    )}
                    {order.specialInstructions && (
                      <div className="bg-brand-light border border-brand-dark rounded-2xl p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <SafeIcon icon={FiClipboard} className="w-3.5 h-3.5 text-accent-gold" />
                          <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">Special Instructions</p>
                        </div>
                        <p className="text-xs text-text-main italic leading-relaxed">"{order.specialInstructions}"</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment summary */}
                <div className="bg-white rounded-2xl p-5 border border-brand-dark">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-3">Payment Summary</h4>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-text-muted">Subtotal</span>
                    <span className="text-sm font-bold text-accent-navy">{formatCurrency(order.total)}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-text-muted">Shipping</span>
                    <span className="text-xs text-green-600 font-bold">Free</span>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-brand-dark/20">
                    <span className="text-sm font-bold text-accent-navy">Total</span>
                    <span className="text-lg font-serif text-accent-gold">{formatCurrency(order.total)}</span>
                  </div>
                  <div className="mt-3 pt-3 border-t border-brand-dark/20">
                    <p className="text-[10px] text-text-muted">
                      Paid via <span className="font-bold capitalize">{order.payment.gateway}</span> · Ref: {order.payment.transactionId}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const OrderTracking = () => {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('all');

  const fetchOrders = () => {
    setLoading(true);
    const all = orderTrackingService.getOrders();
    setOrders(all);
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, [user]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const matchesSearch =
        !searchTerm ||
        order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.items.some(item => item.name?.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStage = filterStage === 'all' || order.currentStage === filterStage;
      return matchesSearch && matchesStage;
    });
  }, [orders, searchTerm, filterStage]);

  // Stats
  const stats = useMemo(() => {
    const active = orders.filter(o => o.currentStage !== 'delivered').length;
    const delivered = orders.filter(o => o.currentStage === 'delivered').length;
    const totalSpent = orders.reduce((sum, o) => sum + (o.total || 0), 0);
    return { total: orders.length, active, delivered, totalSpent };
  }, [orders]);

  if (loading) {
    return (
      <div className="pt-32 min-h-screen bg-brand-light flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-accent-gold border-t-accent-navy rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-3xl mb-10"
        >
          <div className="inline-flex items-center gap-2 bg-accent-gold/10 text-accent-gold px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-6 border border-accent-gold/20">
            <SafeIcon icon={FiTruck} /> Delivery Tracking
          </div>
          <h1 className="text-4xl md:text-6xl font-serif text-accent-navy mb-4">My Orders</h1>
          <p className="text-text-muted text-lg font-light leading-relaxed">
            Track the status of your marketplace purchases from order to delivery.
          </p>
        </motion.div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: 'Total Orders', value: stats.total, icon: FiShoppingBag, color: 'text-blue-500', bg: 'bg-blue-50' },
            { label: 'In Transit',   value: stats.active, icon: FiTruck, color: 'text-amber-500', bg: 'bg-amber-50' },
            { label: 'Delivered',    value: stats.delivered, icon: FiCheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
            { label: 'Total Spent',  value: formatCurrency(stats.totalSpent), icon: FiBox, color: 'text-accent-gold', bg: 'bg-brand-light' },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="bg-white p-5 rounded-[28px] border border-brand-dark shadow-sm"
            >
              <div className={`w-10 h-10 ${s.bg} ${s.color} rounded-2xl flex items-center justify-center mb-3`}>
                <SafeIcon icon={s.icon} className="w-5 h-5" />
              </div>
              <p className="text-2xl font-serif text-accent-navy">{s.value}</p>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">{s.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Search + filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-5 h-5" />
            <input
              type="text"
              placeholder="Search by order ID or product name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white border border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-sm"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            <button
              onClick={() => setFilterStage('all')}
              className={`px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 shrink-0 transition-all ${
                filterStage === 'all' ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
              }`}
            >
              All
            </button>
            {DELIVERY_STAGES.map(stage => (
              <button
                key={stage.key}
                onClick={() => setFilterStage(stage.key)}
                className={`px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 shrink-0 transition-all ${
                  filterStage === stage.key ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                }`}
              >
                {stage.label}
              </button>
            ))}
          </div>
        </div>

        {/* Orders list */}
        {filteredOrders.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-24 bg-white rounded-[40px] border-2 border-dashed border-brand-dark"
          >
            <SafeIcon icon={FiPackage} className="w-20 h-20 text-text-muted/10 mx-auto mb-6" />
            <h3 className="text-2xl font-serif text-accent-navy mb-2">
              {orders.length === 0 ? 'No Orders Yet' : 'No Matching Orders'}
            </h3>
            <p className="text-text-muted text-sm font-light max-w-sm mx-auto">
              {orders.length === 0
                ? 'When you purchase items from the marketplace, your orders will appear here for tracking.'
                : 'Try adjusting your search or filter to find the order you are looking for.'}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map(order => (
              <OrderCard key={order.orderId} order={order} formatCurrency={formatCurrency} />
            ))}
          </div>
        )}

        {/* Refresh button */}
        {orders.length > 0 && (
          <div className="flex justify-center mt-8">
            <button
              onClick={fetchOrders}
              className="flex items-center gap-2 px-6 py-3 bg-white border border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold hover:text-accent-gold transition-all"
            >
              <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" /> Refresh Orders
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;